import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'

const PrivacyPolicyPage = () => {
    return (
        <Box sx={{ bgcolor: '#fafafa', py: 6 }}>
          <Helmet>
            <title>Privacy Policy — MonadX</title>
            <meta name="description" content="MonadX Privacy Policy — how we collect, use, and protect your data." />
            <meta property="og:title" content="Privacy Policy — MonadX" />
          </Helmet>
          <Container maxWidth="md">
            <Typography variant="h3" gutterBottom>
              Privacy Policy
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Effective Date: December 9, 2025
            </Typography>

            <Typography variant="body1" paragraph>
              MonadX ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you use our services, including our website and mobile apps for iOS and Android.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom>1. Information We Collect</Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Account Information"
                  secondary="We collect your name, email address, and authentication credentials. You may sign up using Email and password, Google account, or Apple ID. Authentication is handled securely via Firebase Authentication and Apple’s Sign In with Apple framework."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Usage Data"
                  secondary="We collect information about your interactions with meal logs, feedback entries, and personalization settings to improve your experience and meal suggestions."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Device Information"
                  secondary="We may collect anonymized data about your device type, operating system, and app version to enhance performance and compatibility. We also generate a hashed device identifier for secure Wallet functionality. This identifier is used only for fraud prevention, purchase verification, and restoring points, and is never used for advertising or cross-app tracking."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Third-Party Services"
                  secondary="We use Apple In-App Purchase for transactions, Firebase Authentication for login, and Google Sign-In for account creation. If analytics services are used, they will be disclosed here."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Advertising"
                  secondary="MonadX does not display advertisements or use third-party advertising networks. We do not collect advertising identifiers for the purpose of serving ads."
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom>2. How We Use Your Information</Typography>
            <List>
              <ListItem><ListItemText primary="Create and manage your account" /></ListItem>
              <ListItem><ListItemText primary="Personalize meal suggestions based on logged feedback" /></ListItem>
              <ListItem><ListItemText primary="Improve our services and user experience" /></ListItem>
              <ListItem><ListItemText primary="Communicate updates, tips, and support" /></ListItem>
              <ListItem><ListItemText primary="Provide and improve MonadX features and personalization" /></ListItem>
              <ListItem><ListItemText primary="Verify purchases and secure Wallet functionality using hashed device identifiers" /></ListItem>
            </List>
            <Typography variant="body2" color="text.secondary" mt={2}>
              We do not sell your personal data to third parties.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom>3. Account Deletion</Typography>
            <Typography variant="body1" paragraph>
              You have the right to delete your account at any time. When you delete your account:
            </Typography>
            <List>
              <ListItem><ListItemText primary="Your personal data will be permanently removed from our systems" /></ListItem>
              <ListItem><ListItemText primary="Any meal history or feedback will be erased" /></ListItem>
              <ListItem><ListItemText primary="You will no longer be able to access MonadX features" /></ListItem>
            </List>
            <Typography variant="body1" paragraph>
              To delete your account, go to <strong>More &gt; Preferences &gt; Delete Account</strong> in the app or contact us at <Link href="mailto:hello@monadx.me">hello@monadx.me</Link>.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom>4. Data Security</Typography>
            <Typography variant="body1" paragraph>
              We implement industry-standard security measures to protect your data, including encryption, secure authentication, and regular audits. Our app uses industry-standard encryption for authentication and data transmission. Hashed device identifiers are stored securely and used only for Wallet functionality and fraud prevention. However, no system is 100% secure, and we encourage you to use strong passwords and keep your login credentials confidential.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom>5. Purchases & Receipts</Typography>
            <Typography variant="body1" paragraph>
              If you make in‑app purchases through Apple’s In-App Purchase system, we may collect and retain purchase receipts, transaction identifiers, hashed device identifiers, and related metadata to verify purchases, prevent fraud, and restore points when appropriate. This information is retained only as long as legally required for financial and compliance purposes.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom>6. Children’s Privacy</Typography>
            <Typography variant="body1" paragraph>
              MonadX is not directed to children under the age of 13. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us to request deletion.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom>7. Changes to This Policy</Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. If we make significant changes, we’ll notify you via email or in-app notification. Continued use of MonadX after changes means you accept the updated policy.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom>8. Contact Us</Typography>
            <Typography variant="body1" paragraph>
              If you have questions or concerns about this Privacy Policy, please reach out:
            </Typography>
            <Typography variant="body1">
              📧 <Link href="mailto:hello@monadx.me">hello@monadx.me</Link><br />
              🌐 <Link href="https://monadx.me" target="_blank" rel="noopener">monadx.me</Link>
            </Typography>
          </Container>
        </Box>
    )
}
export default PrivacyPolicyPage;